import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RowSelectionState } from "@tanstack/react-table";
import { InvoiceFilters } from "@common/contracts/invoice.contract";
import { FiltersTypes } from "@common/types/filters/general";
import { Invoice } from "@common/types/invoice";
import { InvoiceView } from "@common/types/views/invoiceViews";
import { View } from "@common/types/views/views";
import { InvoicesViewContext } from "contexts/InvoicesViewContext";
import { isInvoiceProviderAccount } from "@helpers/invoice";
import {
    FilterTabs,
    FiltersChangeFn,
} from "@components/pageHeader/filters/lib";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { PaginatedInvoicesTableContextProvider } from "@components/invoices/PaginatedInvoicesTableContextProvider";
import { useStandaloneInvoicesView } from "@components/invoices/useStandaloneInvoicesView";
import { createInvoicesViewConfig } from "@components/invoices/views/invoicesView/invoicesViewConfig";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import type { PaginationType } from "@hooks/paginationMode";
import { LockedTaskFilters } from "../Transactions/LockedTaskFilters";
import { getInvoicesLockedActiveFilters } from "./invoicesTaskFilters";
import { restrictViewConfigColumns } from "../shared/taskTableView";
import { omitInvoicesTaskScope } from "./invoicesTaskView";
import { InvoicesTaskAskButton } from "./InvoicesTaskAskButton";
import { InvoicesTaskViewContainer } from "./InvoicesTaskViewContainer";

const NOOP = () => {};
// Rows open no details panel in a task table.
const NO_INVOICE_CLICK = () => {};

interface InvoicesTaskTableProps {
    view: InvoiceView;
    onViewChange: (view: View) => void;
    onFiltersChange: FiltersChangeFn;
    availableFilters: FilterTabs[];
    /** Reset target of column settings. */
    defaultView: InvoiceView;
    /** Resolved invoice list filters, or null when no invoice can match. */
    apiFilters: InvoiceFilters | null;
    /** Restricts column settings to these columns (the task's own columns). */
    allowedColumnIds?: ReadonlySet<string>;
    /** Creator-set criteria of a sticky task, rendered as locked pills. */
    originalFilters?: FiltersTypes;
    /** Extra controls rendered in the table header, before column settings. */
    headerActions?: React.ReactNode;
    emptyView: () => React.ReactNode;
    className?: string;
    tableClassName?: string;
    /** Numbered pages by default; the task body loads more on scroll. */
    paginationType?: PaginationType;
}

/**
 * Invoices table shared by the task body and the task creation preview. It
 * reuses the Invoices page table and bulk actions; persistence of view and
 * filter changes is left to the caller.
 */
export const InvoicesTaskTable: React.FC<InvoicesTaskTableProps> = ({
    view,
    onViewChange,
    onFiltersChange,
    availableFilters,
    defaultView,
    apiFilters,
    allowedColumnIds,
    originalFilters,
    headerActions,
    emptyView,
    className,
    tableClassName,
    paginationType,
}) => {
    const contextValue = useStandaloneInvoicesView();
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const clearSelection = useCallback(() => setRowSelection({}), []);
    const renderSelectionActions = useCallback(
        (selectedInvoices: Invoice[]) => (
            <InvoicesTaskAskButton
                invoices={selectedInvoices}
                onAsked={clearSelection}
            />
        ),
        [clearSelection],
    );

    // A changed query can load a different row set; a stale selection would
    // target rows the user can no longer see.
    const apiFiltersKey = JSON.stringify(apiFilters);
    useEffect(() => {
        setRowSelection({});
    }, [apiFiltersKey]);

    const columnSettingsConfig = useMemo(
        () =>
            restrictViewConfigColumns({
                config: createInvoicesViewConfig(),
                allowedColumnIds,
            }),
        [allowedColumnIds],
    );

    const displayFilters = useMemo(
        () => omitInvoicesTaskScope(view.filters),
        [view.filters],
    );
    const lockedActiveFilters = useMemo(
        () => getInvoicesLockedActiveFilters(originalFilters),
        [originalFilters],
    );

    return (
        <InvoicesViewContext.Provider value={contextValue}>
            <TablePageHeader
                isFullPage
                className={className}
                search={displayFilters.search}
                onSearchChange={(value) => onFiltersChange({ search: value })}
                filters={displayFilters}
                onFiltersChange={onFiltersChange}
                availableFilters={availableFilters}
                accountFilterPredicate={isInvoiceProviderAccount}
                shouldShowKickAccountOption
                counterpartyEntityLabel="Customer"
                counterpartyInvoicesOnly
                hideClearButton
                afterFiltersChildren={
                    originalFilters ? (
                        <LockedTaskFilters
                            lockedActiveFilters={lockedActiveFilters}
                            baseFilters={omitInvoicesTaskScope(originalFilters)}
                            maxVisibleFilters={lockedActiveFilters.length}
                        />
                    ) : undefined
                }
            >
                {headerActions}
                <ColumnSettings
                    view={view}
                    viewConfig={columnSettingsConfig}
                    defaultView={defaultView}
                    onViewChange={onViewChange}
                />
            </TablePageHeader>
            <div className={tableClassName}>
                {apiFilters ? (
                    <PaginatedInvoicesTableContextProvider
                        overlayFilters={apiFilters}
                        paginationType={paginationType}
                    >
                        <InvoicesTaskViewContainer
                            view={view}
                            onInvoiceClick={NO_INVOICE_CLICK}
                            hasConnectedInvoiceAccounts={false}
                            onClearFilters={NOOP}
                            rowSelection={rowSelection}
                            onRowSelectionChange={setRowSelection}
                            onClearSelection={clearSelection}
                            emptyView={emptyView}
                            onViewChange={onViewChange}
                            renderSelectionActions={renderSelectionActions}
                        />
                    </PaginatedInvoicesTableContextProvider>
                ) : (
                    // No invoice can match (an empty snapshot or unreadable
                    // filters). The list must not be queried: an empty filter
                    // set would return every invoice of the workspace.
                    emptyView()
                )}
            </div>
        </InvoicesViewContext.Provider>
    );
};
